import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ViewEncapsulation,
    computed,
    contentChildren,
    effect,
    forwardRef,
    input,
    model,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export const DS_RADIO_SIZE_ARRAY = ['medium', 'large'] as const;
export const DS_RADIO_POSITION_ARRAY = ['left', 'right'] as const;
export type DsRadioSize = (typeof DS_RADIO_SIZE_ARRAY)[number];
export type DsRadioLabelPosition = (typeof DS_RADIO_POSITION_ARRAY)[number];

@Component({
    selector: 'ds-radio-button',
    templateUrl: './radio-button.component.html',
    styleUrls: ['./radio-button.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    host: {
        'class': 'ds-radio-button',
        '[class]': 'hostClass()',
        '[class.ds-radio-selected]': 'checked()',
        '[class.ds-radio-unselected]': '!checked()',
        '[class.ds-radio-disabled]': 'disabled()',
    },
})
export class DsRadioButton {
    public readonly value = input<string>('');
    public readonly name = input.required<string>();
    public readonly disabled = model(false);
    public readonly size = model<DsRadioSize>('large');
    public readonly labelPosition = model<DsRadioLabelPosition>('right');
    public readonly checked = model(false);
    public readonly id = input<string | null>(null);
    public readonly ariaLabel = input<string | null>(null);
    public readonly ariaLabelledby = input<string | null>(null);
    public readonly ariaDescribedby = input<string | null>(null);

    protected readonly hostClass = computed(() => {
        const sizeClass = this.size() === 'large' ? 'ds-radio-large' : 'ds-radio-medium';
        const labelPositionClass = this.labelPosition() === 'left' ? 'ds-radio-label-left' : '';
        return `ds-radio-button ${sizeClass} ${labelPositionClass}`;
    });

    onInputChange() {
        if (!this.checked() && !this.disabled()) {
            this.checked.set(true);
        }
    }

    registerOnInputChange(fn: () => void) {
        this.onInputChange = fn;
    }
}

@Component({
    selector: 'ds-radio-group',
    template: `<ng-content />`,
    host: {
        role: 'radiogroup',
    },
    styleUrls: ['./radio-button.component.scss'],
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DsRadioGroup),
            multi: true,
        },
    ],
})
export class DsRadioGroup implements ControlValueAccessor, AfterContentInit {
    public readonly disabled = model(false);
    public readonly value = model<string>('');
    public readonly size = input<DsRadioSize | null>(null);
    public readonly labelPosition = input<DsRadioLabelPosition | null>(null);

    protected readonly radioButtons = contentChildren(DsRadioButton, { descendants: true });

    private onChange: (value: any) => void = () => {};
    private onTouched: () => void = () => {};

    constructor() {
        effect(() => {
            if (this.radioButtons()) {
                this.updatePositionsAndSizes();
                this.updateButtonsState();
            }
        });
    }

    private updateButtonsState() {
        if (this.radioButtons()) {
            this.radioButtons().forEach((btn) => {
                btn.checked.set(btn.value() === this.value());
                btn.disabled.set(this.disabled() || btn.disabled());
            });
        }
    }

    selectButton(selectedBtn: DsRadioButton) {
        if (!this.disabled() && !selectedBtn.disabled() && this.radioButtons()) {
            this.radioButtons().forEach((btn) => {
                btn.checked.set(btn === selectedBtn);
            });
            this.value.set(selectedBtn.value());
            this.onChange(this.value());
            this.onTouched();
        }
    }

    private updatePositionsAndSizes() {
        this.radioButtons().forEach((button) => {
            if (this.size() != null) button.size.set(this.size() as DsRadioSize);
            if (this.labelPosition() != null) button.labelPosition.set(this.labelPosition() as DsRadioLabelPosition);
        });
    }

    ngAfterContentInit() {
        if (this.radioButtons()) {
            this.updatePositionsAndSizes();
            this.updateButtonsState();
            this.radioButtons().forEach((button) => {
                button.registerOnInputChange(() => {
                    this.selectButton(button);
                });
            });
        }
    }

    writeValue(value: string): void {
        this.value.set(value);
    }

    registerOnChange(fn: (value: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled.set(isDisabled);
    }
}
